import React, { useState, useCallback, useEffect } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import Modal from '../Modal';
import ProgramSiteSelector from '../ProgramSiteSelector';
import { Button } from '@mes-amis/galette-design-system';
import db from '@/db';
import { Document } from '@/db/types/document';
import { dispatchDataEvent, DataEvents } from '@/db/events';
import { isOnline } from '@/lib/connectivity';
import { useServerOnline } from '@/hooks/useServerOnline';
import { pullTemplate } from '@/db/services/document_template_service';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface ChangeProgramSiteModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: Document | null;
  mode: 'change' | 'permission_error';
  onSaved?: () => void;
}

export default function ChangeProgramSiteModal({
  isOpen,
  onClose,
  document,
  mode,
  onSaved,
}: ChangeProgramSiteModalProps) {
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const online = useServerOnline();

  const template = useLiveQuery(async () => {
    if (!document?.template_id) return null;
    return db.documentTemplates.get(document.template_id) ?? null;
  }, [document?.template_id]);
  const isProgramDisabled = template?.program_disabled === true;

  // Reset state and refresh programs/sites when modal opens
  useEffect(() => {
    if (isOpen && document) {
      setSelectedProgramId(document.program_id ?? null);
      setSelectedSiteId(document.site_id ?? null);
      setSaving(false);
      setRetrying(false);

      if (isOnline()) {
        dispatchDataEvent(DataEvents.REFRESH_PROGRAMS).catch(err => {
          console.error('Failed to refresh programs on modal open:', err);
        });
        dispatchDataEvent(DataEvents.REFRESH_SITES).catch(err => {
          console.error('Failed to refresh sites on modal open:', err);
        });
        if (document.template_id) {
          void pullTemplate(document.template_id).catch(err => {
            console.error('Failed to refresh template on modal open:', err);
          });
        }
      }
    }
  }, [isOpen, document]);

  const handleSave = useCallback(async () => {
    if (!document?.id) return;
    setSaving(true);

    try {
      await db.documents.update(document.id, {
        program_id: selectedProgramId ?? undefined,
        site_id: selectedSiteId ?? undefined,
        sync_error_type: null,
        sync_error_message: null,
        sync_error_fingerprint: null,
        sync_error_at: null,
        sync_error_stage: null,
        synced: false,
      });

      // Fire sync in the background — may throw offline; global sync will retry.
      void dispatchDataEvent(DataEvents.SYNC_DOCUMENT, { documentId: document.id }).catch(err => {
        console.error('Background sync after program/site save failed:', err);
      });

      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Failed to update document program/site:', err);
    } finally {
      setSaving(false);
    }
  }, [document, selectedProgramId, selectedSiteId, onClose, onSaved]);

  const handleRetrySync = useCallback(async () => {
    if (!document?.id || !isOnline()) return;
    setRetrying(true);

    try {
      // Clear the sync error and mark as unsynced so syncDocument picks it up
      await db.documents.update(document.id, {
        program_id: selectedProgramId ?? undefined,
        site_id: selectedSiteId ?? undefined,
        sync_error_type: null,
        sync_error_message: null,
        sync_error_fingerprint: null,
        sync_error_at: null,
        sync_error_stage: null,
        synced: false,
      });

      const syncedDoc = await dispatchDataEvent<typeof DataEvents.SYNC_DOCUMENT, Document | null>(
        DataEvents.SYNC_DOCUMENT,
        { documentId: document.id }
      );

      // Close only on a real success (server returned 200). A repeat permission
      // error (or any other sync_error_type) keeps the modal open so the user
      // can change the program/site and retry.
      if (syncedDoc?.synced === true && !syncedDoc.sync_error_type) {
        onSaved?.();
        onClose();
      }
    } catch (err) {
      console.error('Failed to retry sync:', err);
    } finally {
      setRetrying(false);
    }
  }, [document, selectedProgramId, selectedSiteId, onClose, onSaved]);

  if (!document) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isProgramDisabled ? 'Edit Site' : 'Edit Program/Site'}
      maxWidth="md"
    >
      <div className="space-y-6">
        {mode === 'permission_error' && (
          <div className="flex gap-3 rounded-lg bg-red-50 border border-red-200 p-4">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
            <div className="text-sm text-red-800">
              <p className="font-medium mb-1">Sync Error</p>
              <p>
                This document could not be synced because you don&apos;t have permission to edit
                documents for the selected program/site. Please change the program/site below
                or contact your administrator.
              </p>
            </div>
          </div>
        )}

        <ProgramSiteSelector
          clientId={document.client_id}
          templateId={document.template_id}
          selectedProgramId={selectedProgramId}
          selectedSiteId={selectedSiteId}
          onProgramChange={setSelectedProgramId}
          onSiteChange={setSelectedSiteId}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>

          {mode === 'permission_error' && (
            <div className="relative group">
              <Button
                variant="primary"
                onClick={() => void handleRetrySync()}
                disabled={!online || retrying}
              >
                {retrying ? 'Retrying...' : 'Save & Retry Sync'}
              </Button>
              {!online && (
                <div className="absolute bottom-full mb-2 right-0 px-3 py-1.5 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  You must be online to retry sync
                </div>
              )}
            </div>
          )}

          {mode === 'change' && (
            <Button
              variant="primary"
              onClick={() => void handleSave()}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}
